import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  MessageBar,
  RoleGuard,
  Table,
  Tooltip,
  useTable,
} from 'portal-commons';
import React, { useMemo, useState, useEffect, FunctionComponent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTrash } from '@fortawesome/pro-regular-svg-icons';

import { DetailsSection, Divider } from './components';
import { PatentOfficeNames } from '../../constants';
import { getBadgeTypeByStatus } from '../../utils';
import { Trademark, trademarkController } from '@/apis/core';
import { humanize } from '@/utils/string';
import { convertTimeWithTimezone } from '@/utils/time';
import { toastFlashMessage } from '@/utils';

interface TrademarkVerificationTabProps {
  readOnly?: boolean;
  brandUid: string;
  onApply?: () => void;
  onDataChange?: (trademarks: Trademark[]) => unknown;
}

const TableHeaders = [
  {
    id: 'registrationId',
    label: 'Registration Number',
    sortable: false,
  },
  {
    id: 'trademarkText',
    label: 'Trademark Wording',
    sortable: false,
  },
  {
    id: 'owner',
    label: 'Trademark Owner',
    sortable: false,
  },
  {
    id: 'patentOffice',
    label: 'Trademark Office',
    sortable: false,
  },
  {
    id: 'validUntil',
    label: 'Expiration Date',
    sortable: false,
  },
  {
    id: 'outcome',
    label: 'Status',
    sortable: false,
  },
];

const TrademarkVerificationTab: FunctionComponent<
  TrademarkVerificationTabProps
> = ({ readOnly = false, brandUid, onApply, onDataChange }) => {
  const [proceeding, setProceeding] = useState(false);
  const [deletingTrademark, setDeletingTrademark] = useState<Trademark | null>(
    null
  );

  const {
    loading: tableLoading,
    tableData: verifications,
    reload,
  } = useTable<Trademark>(
    async () => {
      const records =
        brandUid && !readOnly
          ? await trademarkController.getTrademarks(brandUid)
          : [];
      return {
        data: records,
        total: records.length,
        page: 1,
        rowsPerPage: Math.max(records.length, 10),
      };
    },
    {
      deps: [brandUid],
      inMemory: true,
    }
  );

  const deleteTrademark = async () => {
    if (!deletingTrademark || proceeding) return;
    setProceeding(true);
    setDeletingTrademark(null);
    const response = await trademarkController.deleteTrademark(
      brandUid,
      deletingTrademark.uid
    );
    setProceeding(false);
    if (response) {
      toastFlashMessage(
        `Trademark ${deletingTrademark.trademarkText} successfully deleted`,
        'success'
      );
      reload();
    }
  };

  const tableDisplayData = useMemo(() => {
    return verifications.map((d) => ({
      registrationId: (
        <span data-testid="registrationId">{d.registrationId || 'N/A'}</span>
      ),
      trademarkText: (
        <span data-testid="trademarkText">{d.trademarkText || 'N/A'}</span>
      ),
      owner: <span data-testid="owner">{d.owner || 'N/A'}</span>,
      patentOffice: (
        <span data-testid="patentOffice" title={d.patentOffice}>
          {d.patentOffice
            ? (PatentOfficeNames as Record<string, string>)[d.patentOffice] ??
              d.patentOffice
            : 'N/A'}
        </span>
      ),
      validUntil: (
        <span data-testid="validUntil">
          {d.validUntil
            ? convertTimeWithTimezone(d.validUntil, undefined, 'MM/DD/YYYY')
            : 'N/A'}
        </span>
      ),
      outcome: (
        <Flex sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          {humanize(d.outcome) ? (
            <Badge
              type={getBadgeTypeByStatus(d.outcome)}
              endAdornment={
                d.description ? (
                  <Tooltip
                    style={{
                      width: '12px',
                      color: 'inherit',
                      fontWeight: 400,
                      fontSize: '10px',
                    }}
                    title={d.description}
                  >
                    <FontAwesomeIcon icon={faInfoCircle} />
                  </Tooltip>
                ) : undefined
              }
              data-testid="outcome"
            >
              {humanize(d.outcome)}
            </Badge>
          ) : (
            <span data-testid="outcome">N/A</span>
          )}
          <RoleGuard feature="brandDetail.manageTrademark">
            <Box
              sx={{
                display: tableLoading || proceeding ? 'none' : 'block',
                padding: '0 16px',
                color: 't.black80',
                fontSize: 'H400',
                cursor: 'pointer',
              }}
              onClick={() => setDeletingTrademark(d)}
              data-testid="deleteTrademarkVerification"
            >
              <FontAwesomeIcon icon={faTrash} />
            </Box>
          </RoleGuard>
        </Flex>
      ),
    }));
  }, [verifications, tableLoading, proceeding]);

  useEffect(() => {
    onDataChange?.(verifications);
  }, [verifications]);

  return (
    <>
      <DetailsSection
        description={
          'Trademark verification must be completed before sending a letter of authorization.\nOnly Verified trademarks will be populated in the letter of authorization.'
        }
      >
        <Table
          disableHover
          loading={tableLoading}
          headRows={TableHeaders}
          data={tableDisplayData}
          emptyState="no trademarks to view"
          emptyBlockHeight="180px"
        />
        {deletingTrademark &&
          (Array.isArray(deletingTrademark.associatedSenderIdUidList) &&
          deletingTrademark.associatedSenderIdUidList.length > 0 ? (
            <MessageBar
              type="warning"
              title="Trademark Associated to Sender ID(s)"
              showClose={false}
              primaryAction={{
                label: 'Cancel',
                onClick: () => {
                  setDeletingTrademark(null);
                },
              }}
              data-testid="deleteTrademarkConfirmation"
            >
              This trademark cannot be deleted because it is currently being
              used by one or more registered Sender IDs (e.g.,{' '}
              {deletingTrademark.associatedSenderIdUidList
                .map((d) => d.senderId)
                .join(', ')}
              ). To delete this trademark, you must first remove its association
              with the Sender ID(s) on the Sender ID tab.
            </MessageBar>
          ) : (
            <MessageBar
              type="error"
              title={`Delete Trademark, ${deletingTrademark.trademarkText}?`}
              showClose={false}
              primaryAction={{
                label: 'Delete Trademark',
                onClick: () => {
                  deleteTrademark();
                },
              }}
              secondaryAction={{
                label: 'Cancel',
                onClick: () => setDeletingTrademark(null),
              }}
              data-testid="deleteTrademarkConfirmation"
            >
              This action cannot be undone. This trademark will be permanently
              removed from this brand's profile. If you decide to register this
              trademark again in the future, it must undergo the automated
              verification check again.
            </MessageBar>
          ))}
      </DetailsSection>
      <Divider />
      <Flex
        sx={{
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: '24px',
        }}
      >
        <RoleGuard feature="brandDetail.manageTrademark">
          <Button
            size="lg"
            shape="square"
            variant="outline"
            color="secondary"
            disabled={readOnly}
            onClick={onApply}
            data-testid="applyTrademarkVerification"
          >
            Apply for Trademark Verification
          </Button>
        </RoleGuard>
      </Flex>
    </>
  );
};

export default TrademarkVerificationTab;
